import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

import { ApiError } from '@starling/core/api/errors';
import {
  ChoiceRow,
  FileField,
  FormError,
  GhostButton,
  Hint,
  PrimaryButton,
  SelectField,
  TextAreaField,
  TextField,
} from '@/components/form/primitives';
import { formatMoney } from '@/lib/format';
import { whatsappUrl } from '@/lib/whatsapp';
import { useMyOrganizationsQuery, type MyOrganization } from '@/organization/hooks';
import { GateCard, canPublish, type GateKind } from '@/post/PostPage';
import {
  AD_WEEKS,
  IMAGE_ACCEPT,
  findAdWeekPrice,
  useCreateAdMutation,
  useUploadImageMutation,
  useVisionPricingQuery,
  type AdWeeks,
  type MediaImage,
  type PriceItem,
} from './hooks';
import pageStyles from '@/post/PostPage.module.css';
import styles from './NewAdPage.module.css';

// Заявка на рекламу (V1.4, бриф 20260911-design-brief-ads §2) — lightweight
// экран из примитивов PostFlow. Гейт — тот же, что у публикации объявления
// (`canPublish` + `GateCard` из PostPage): нет организации → «нужна
// организация», ни одной verified → «подтвердите». Подтверждённый контакт
// здесь НЕ требуется — ссылка баннера может вести на сайт.
//   * баннер грузится заранее в платформенные медиа (`POST /media/images`),
//     в заявку уходит `image_id`; превью — из ответа загрузки;
//   * ссылка — переключатель «Сайт / WhatsApp / Telegram»: последние два
//     подставляют wa.me / t.me из контактов организации и недоступны без них;
//   * срок 1/2/4 недели, сумма «= N ₸» по публичному тарифу `ad_week`;
//     тарифа нет (услуга выключена) → форма не отправляется;
//   * финал — «Заявка отправлена на модерацию» + «Мои заявки» / «На главную».

type LinkKind = 'site' | 'whatsapp' | 'telegram';
type FieldError = 'title' | 'image' | 'link' | null;

const TITLE_MAX = 60;
const COMMENT_MAX = 500;

/** Ссылка баннера по выбранному варианту (пусто — вариант недоступен/не заполнен). */
function buildLinkUrl(kind: LinkKind, siteUrl: string, org: MyOrganization | undefined): string {
  if (kind === 'site') return siteUrl.trim();
  if (kind === 'whatsapp') return org?.phone && org.whatsapp ? whatsappUrl(org.phone) : '';
  return org?.telegram ? `https://t.me/${org.telegram.replace(/^@/, '')}` : '';
}

function DoneCard(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className={pageStyles.card}>
      <div className={pageStyles.success}>
        <div className={pageStyles.successIcon}>
          <Check size={24} aria-hidden="true" />
        </div>
        <p className={pageStyles.successText}>{t('ads.new.done.title')}</p>
        <p className={styles.doneText}>{t('ads.new.done.text')}</p>
        <div className={pageStyles.doneLinks}>
          <Link to="/my/ads" className={pageStyles.primaryLink}>
            {t('ads.new.done.myAds')}
          </Link>
          <Link to="/" className={pageStyles.secondaryLink}>
            {t('ads.new.done.home')}
          </Link>
        </div>
      </div>
    </div>
  );
}

/** Превью загруженного баннера + «заменить». */
function BannerPreview({ image, onReplace }: { image: MediaImage; onReplace: () => void }) {
  const { t } = useTranslation();
  return (
    <div className={styles.preview}>
      <img
        className={styles.previewImage}
        src={image.url}
        alt={t('ads.new.form.bannerPreview')}
        width={image.width}
        height={image.height}
      />
      <div className={styles.previewMeta}>
        <span>
          {image.width}×{image.height}
        </span>
        <GhostButton onClick={onReplace}>{t('ads.new.form.bannerReplace')}</GhostButton>
      </div>
    </div>
  );
}

function NewAdForm({
  organizations,
  price,
  onSubmitted,
  onGate,
}: {
  organizations: MyOrganization[];
  price: PriceItem;
  onSubmitted: () => void;
  onGate: (kind: GateKind) => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const createMutation = useCreateAdMutation();
  const uploadMutation = useUploadImageMutation();

  const [organizationId, setOrganizationId] = useState(organizations[0]?.id ?? '');
  const [title, setTitle] = useState('');
  const [image, setImage] = useState<MediaImage | null>(null);
  // Ключ файлового поля: после «заменить» перерисовываем input, чтобы выбор
  // того же файла снова вызвал onChange.
  const [fileFieldKey, setFileFieldKey] = useState(0);
  const [linkKind, setLinkKind] = useState<LinkKind>('site');
  const [siteUrl, setSiteUrl] = useState('');
  const [weeks, setWeeks] = useState<AdWeeks>(1);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<FieldError>(null);

  const org = organizations.find((o) => o.id === organizationId);
  const whatsappAvailable = Boolean(org?.phone && org.whatsapp);
  const telegramAvailable = Boolean(org?.telegram);
  const linkUrl = buildLinkUrl(linkKind, siteUrl, org);
  const total = price.amount_kzt * weeks;

  const ready = Boolean(
    organizationId &&
    title.trim() &&
    image &&
    linkUrl &&
    !uploadMutation.isPending &&
    !createMutation.isPending,
  );

  const selectOrganization = (nextId: string): void => {
    setOrganizationId(nextId);
    // Контакты у другой организации свои — вариант мог стать недоступным.
    const next = organizations.find((o) => o.id === nextId);
    if (linkKind === 'whatsapp' && !(next?.phone && next.whatsapp)) setLinkKind('site');
    if (linkKind === 'telegram' && !next?.telegram) setLinkKind('site');
  };

  const selectFile = (file: File): void => {
    setError(null);
    setFieldError(null);
    uploadMutation.mutate(file, {
      onSuccess: setImage,
      onError: (err) => {
        setImage(null);
        setFieldError('image');
        if (err instanceof ApiError) {
          if (err.status === 413) return setError(t('ads.new.errors.imageTooLarge'));
          if (err.status === 415) return setError(t('ads.new.errors.imageUnsupported'));
          if (err.code === 'image_too_small') return setError(t('ads.new.errors.imageTooSmall'));
        }
        setError(t('ads.new.errors.uploadGeneric'));
      },
    });
  };

  const submit = (): void => {
    if (!ready || !image) return;
    setError(null);
    setFieldError(null);
    createMutation.mutate(
      {
        organization_id: organizationId,
        title: title.trim(),
        image_id: image.id,
        link_url: linkUrl,
        weeks,
        comment: comment.trim() || null,
      },
      {
        onSuccess: onSubmitted,
        onError: (err) => {
          if (err instanceof ApiError) {
            // Гейт организации сменился после загрузки формы — та же подводка,
            // что и до формы (403 not_organization_member / organization_not_verified
            // / organization_disabled).
            if (err.status === 403) return onGate('notVerified');
            if (err.code === 'invalid_url') {
              setFieldError('link');
              return setError(t('ads.new.errors.invalidUrl'));
            }
            if (err.code === 'image_not_found') {
              setImage(null);
              setFileFieldKey((k) => k + 1);
              setFieldError('image');
              return setError(t('ads.new.errors.imageNotFound'));
            }
          }
          setError(t('ads.new.errors.generic'));
        },
      },
    );
  };

  return (
    <div className={pageStyles.card}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {organizations.length > 1 && (
          <SelectField
            label={t('ads.new.form.organization')}
            value={organizationId}
            onChange={selectOrganization}
            name="organization"
            options={organizations.map((o) => ({ value: o.id, label: o.name }))}
          />
        )}

        <TextField
          label={t('ads.new.form.title')}
          value={title}
          onChange={setTitle}
          name="title"
          maxLength={TITLE_MAX}
          placeholder={t('ads.new.form.titlePlaceholder')}
        />

        {image ? (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>{t('ads.new.form.banner')}</span>
            <BannerPreview
              image={image}
              onReplace={() => {
                setImage(null);
                setFileFieldKey((k) => k + 1);
              }}
            />
          </div>
        ) : (
          <FileField
            key={fileFieldKey}
            label={t('ads.new.form.banner')}
            accept={IMAGE_ACCEPT}
            name="banner"
            disabled={uploadMutation.isPending}
            onSelect={selectFile}
            hint={
              uploadMutation.isPending
                ? t('ads.new.form.bannerUploading')
                : t('ads.new.form.bannerHint')
            }
          />
        )}
        {fieldError === 'image' && <FormError>{error}</FormError>}

        <div className={styles.field}>
          <span className={styles.fieldLabel}>{t('ads.new.form.link')}</span>
          <ChoiceRow<LinkKind>
            value={linkKind}
            onChange={(next) => {
              setLinkKind(next);
              if (fieldError === 'link') setFieldError(null);
            }}
            label={t('ads.new.form.link')}
            options={[
              { value: 'site', label: t('ads.new.form.linkSite') },
              {
                value: 'whatsapp',
                label: t('ads.new.form.linkWhatsapp'),
                disabled: !whatsappAvailable,
              },
              {
                value: 'telegram',
                label: t('ads.new.form.linkTelegram'),
                disabled: !telegramAvailable,
              },
            ]}
          />
          {linkKind === 'site' && (
            <TextField
              label={t('ads.new.form.siteUrl')}
              value={siteUrl}
              onChange={setSiteUrl}
              type="url"
              inputMode="url"
              name="link_url"
              maxLength={500}
              placeholder="https://"
            />
          )}
          {linkKind !== 'site' && linkUrl && (
            <Hint>
              {t('ads.new.form.linkResolved')} <span className={styles.mono}>{linkUrl}</span>
            </Hint>
          )}
          {(!whatsappAvailable || !telegramAvailable) && (
            <Hint>
              {!whatsappAvailable && t('ads.new.form.whatsappUnavailable')}
              {!whatsappAvailable && !telegramAvailable && ' '}
              {!telegramAvailable && t('ads.new.form.telegramUnavailable')}{' '}
              <Link to="/my/organization" className={styles.hintLink}>
                {t('post.gate.action')}
              </Link>
            </Hint>
          )}
        </div>
        {fieldError === 'link' && <FormError>{error}</FormError>}

        <div className={styles.field}>
          <span className={styles.fieldLabel}>{t('ads.new.form.weeks')}</span>
          <div className={styles.weeksRow}>
            <div className={styles.weeksChoice}>
              <ChoiceRow<AdWeeks>
                value={weeks}
                onChange={setWeeks}
                label={t('ads.new.form.weeks')}
                options={AD_WEEKS.map((w) => ({
                  value: w,
                  label: t('ads.new.form.weeksOption', { count: w }),
                }))}
              />
            </div>
            <span className={styles.total} data-testid="ad-total">
              = {formatMoney(total, i18n.language)} ₸
            </span>
          </div>
          <Hint>
            {t('ads.new.form.priceHint', { amount: formatMoney(price.amount_kzt, i18n.language) })}
          </Hint>
        </div>

        <TextAreaField
          label={t('ads.new.form.comment')}
          value={comment}
          onChange={setComment}
          name="comment"
          maxLength={COMMENT_MAX}
          placeholder={t('ads.new.form.commentPlaceholder')}
        />

        {fieldError === null && <FormError>{error}</FormError>}
        <PrimaryButton disabled={!ready}>
          {createMutation.isPending ? t('ads.new.form.submitting') : t('ads.new.form.submit')}
        </PrimaryButton>
      </form>
    </div>
  );
}

export function NewAdPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const orgsQuery = useMyOrganizationsQuery();
  const pricingQuery = useVisionPricingQuery();
  const [submitted, setSubmitted] = useState(false);
  // Гейт с ответа сервера (403 при отправке) — перебивает гейт по списку.
  const [serverGate, setServerGate] = useState<GateKind | null>(null);

  let body: JSX.Element;
  if (submitted) {
    body = <DoneCard />;
  } else if (serverGate) {
    body = <GateCard kind={serverGate} />;
  } else if (orgsQuery.isPending || pricingQuery.isPending) {
    body = <div className={pageStyles.state}>{t('ads.new.loading')}</div>;
  } else if (orgsQuery.isError || pricingQuery.isError) {
    body = (
      <div className={`${pageStyles.state} ${pageStyles.stateError}`}>
        {t('ads.new.error')}
        <div>
          <button
            type="button"
            className={pageStyles.retry}
            onClick={() => {
              void orgsQuery.refetch();
              void pricingQuery.refetch();
            }}
          >
            {t('ads.new.retry')}
          </button>
        </div>
      </div>
    );
  } else {
    const items = orgsQuery.data.items;
    const verified = items.filter(canPublish);
    const price = findAdWeekPrice(pricingQuery.data);
    if (items.length === 0) {
      body = <GateCard kind="noOrg" />;
    } else if (verified.length === 0) {
      body = <GateCard kind="notVerified" />;
    } else if (!price) {
      // Тариф не заведён или услуга выключена в настройках платформы.
      body = (
        <div className={pageStyles.card}>
          <h2 className={pageStyles.cardTitle}>{t('ads.new.unavailable.title')}</h2>
          <p className={pageStyles.gateText}>{t('ads.new.unavailable.text')}</p>
          <Link to="/" className={pageStyles.secondaryLink}>
            {t('ads.new.done.home')}
          </Link>
        </div>
      );
    } else {
      body = (
        <NewAdForm
          organizations={verified}
          price={price}
          onSubmitted={() => setSubmitted(true)}
          onGate={setServerGate}
        />
      );
    }
  }

  return (
    <div className={pageStyles.wrap}>
      <div className={pageStyles.titleRow}>
        <h1 className={pageStyles.pageTitle}>{t('ads.new.title')}</h1>
        <button
          type="button"
          className={pageStyles.close}
          aria-label={t('post.close')}
          onClick={() => navigate(-1)}
        >
          ×
        </button>
      </div>
      {body}
    </div>
  );
}
